import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Inventory from '@/pages/Inventory';
import ProductDetail from '@/pages/ProductDetail';
import PurchaseOrders from '@/pages/PurchaseOrders';
import SupplierOrders from '@/pages/SupplierOrders';
import ShoppingCart from '@/pages/ShoppingCart';
import WarehouseReceptions from '@/pages/WarehouseReceptions';
import Warehouses from '@/pages/Warehouses';
import TechConsumption from '@/pages/TechConsumption';
import ConsumptionInvoicing from '@/pages/ConsumptionInvoicing';
import CockpitCompras from '@/pages/CockpitCompras';
import VanReplenishment from '@/pages/VanReplenishment';
import VanPermanentStock from '@/pages/VanPermanentStock';
import StoreTransfer from '@/pages/StoreTransfer';
import UserManagement from '@/pages/UserManagement';
import StockAlerts from '@/pages/StockAlerts';
import MyVanStock from '@/pages/MyVanStock';
import Settings from '@/pages/Settings';
import SyncManagement from '@/pages/SyncManagement';
import DataQuality from '@/pages/DataQuality';
import ProcurementConfig from '@/pages/ProcurementConfig';
import PurchaseUnits from '@/pages/PurchaseUnits';
import Help from '@/pages/Help';
import AuditLog from '@/pages/AuditLog';
import GoLiveAdmin from '@/pages/GoLiveAdmin';
import Breakage from '@/pages/Breakage';
import BreakageHistory from '@/pages/BreakageHistory';
import FSMServiceAppointments from '@/pages/FSMServiceAppointments';
import FSMAdmin from '@/pages/FSMAdmin';
import Layout from '@/components/Layout';
import { NotificationProvider } from '@/context/NotificationContext';

// Retail Analytics pages
import RetailDashboard from '@/pages/retail/RetailDashboard';
import RetailVendas from '@/pages/retail/RetailVendas';
import RetailAnalises from '@/pages/retail/RetailAnalises';
import RetailCustos from '@/pages/retail/RetailCustos';
import RetailFechoCaixa from '@/pages/retail/RetailFechoCaixa';
import RetailDepositos from '@/pages/retail/RetailDepositos';
import RetailConfigAdmin from '@/pages/retail/RetailConfigAdmin';
import RetailAuditLog from '@/pages/retail/RetailAuditLog';
import RetailControleDiario from '@/pages/retail/RetailControleDiario';
import CompanySettings from '@/pages/CompanySettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext();

// Route protection — supports both legacy `allowedRoles` and new `requirePerm`
const RoleProtectedRoute = ({ children, allowedRoles, requirePerm, user }) => {
  // Admin always passes
  if (user?.role === 'admin') return children;

  // New permission-based check
  if (requirePerm) {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    if (perms.includes(requirePerm)) return children;
    if (user?.role === 'technician') return <Navigate to="/fsm-appointments" replace />;
    return <Navigate to="/" replace />;
  }

  // Legacy role check
  if (!allowedRoles) return children;
  const userRole = user?.role || 'technician';
  if (allowedRoles.includes(userRole)) return children;
  if (userRole === 'technician') return <Navigate to="/fsm-appointments" replace />;
  return <Navigate to="/" replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      // Refresh user from /me to pick up server-side permission/scope changes
      axios.get(`${API}/auth/me`).then((r) => {
        const fresh = r.data;
        setUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
      }).catch(() => {
        // token invalid → logout
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setToken(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  };

  const hasAnyPermission = (perms) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!Array.isArray(user.permissions)) return false;
    return perms.some((p) => user.permissions.includes(p));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, hasPermission, hasAnyPermission }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to={user?.role === 'technician' ? '/fsm-appointments' : '/'} />} />
          <Route
            path="/"
            element={user ? (
              <NotificationProvider>
                <Layout />
              </NotificationProvider>
            ) : <Navigate to="/login" />}
          >
            <Route index element={
              <RoleProtectedRoute requirePerm="alerts.view" user={user}>
                <Dashboard />
              </RoleProtectedRoute>
            } />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/:productId" element={<ProductDetail />} />
            <Route path="purchase-orders" element={
              <RoleProtectedRoute requirePerm="po.view" user={user}>
                <PurchaseOrders />
              </RoleProtectedRoute>
            } />
            <Route path="supplier-orders" element={
              <RoleProtectedRoute requirePerm="suppliers.view" user={user}>
                <SupplierOrders />
              </RoleProtectedRoute>
            } />
            <Route path="shopping-cart" element={
              <RoleProtectedRoute requirePerm="cart.view" user={user}>
                <ShoppingCart />
              </RoleProtectedRoute>
            } />
            <Route path="cockpit" element={
              <RoleProtectedRoute requirePerm="po.view" user={user}>
                <CockpitCompras />
              </RoleProtectedRoute>
            } />
            <Route path="receptions" element={
              <RoleProtectedRoute requirePerm="receptions.view" user={user}>
                <WarehouseReceptions />
              </RoleProtectedRoute>
            } />
            <Route path="warehouses" element={
              <RoleProtectedRoute requirePerm="warehouses.view" user={user}>
                <Warehouses />
              </RoleProtectedRoute>
            } />
            <Route path="consumptions" element={<TechConsumption />} />
            <Route path="fsm-services" element={<FSMServiceAppointments />} />
            <Route path="fsm-appointments" element={<FSMServiceAppointments />} />
            <Route path="my-stock" element={<MyVanStock />} />
            <Route path="invoicing" element={
              <RoleProtectedRoute requirePerm="invoicing.view" user={user}>
                <ConsumptionInvoicing />
              </RoleProtectedRoute>
            } />
            <Route path="van-replenishment" element={
              <RoleProtectedRoute requirePerm="van.replenish" user={user}>
                <VanReplenishment />
              </RoleProtectedRoute>
            } />
            <Route path="van-permanent-stock" element={
              <RoleProtectedRoute requirePerm="van.replenish" user={user}>
                <VanPermanentStock />
              </RoleProtectedRoute>
            } />
            <Route path="store-transfer" element={
              <RoleProtectedRoute requirePerm="tar.create_from_own" user={user}>
                <StoreTransfer />
              </RoleProtectedRoute>
            } />
            <Route path="users" element={
              <RoleProtectedRoute requirePerm="users.view" user={user}>
                <UserManagement />
              </RoleProtectedRoute>
            } />
            <Route path="alerts" element={
              <RoleProtectedRoute requirePerm="alerts.view" user={user}>
                <StockAlerts />
              </RoleProtectedRoute>
            } />
            {/* Legacy routes - redirect to new consolidated page */}
            <Route path="predictions" element={<Navigate to="/alerts" replace />} />
            <Route path="reports" element={<Navigate to="/" replace />} />
            <Route path="settings" element={<Settings />} />
            <Route path="sync-management" element={
              <RoleProtectedRoute allowedRoles={['admin']} user={user}>
                <SyncManagement />
              </RoleProtectedRoute>
            } />
            <Route path="company-settings" element={
              <RoleProtectedRoute allowedRoles={['admin']} user={user}>
                <CompanySettings />
              </RoleProtectedRoute>
            } />
            <Route path="breakage" element={<Breakage />} />
            <Route path="breakage/history" element={<BreakageHistory />} />
            <Route path="fsm-admin" element={
              <RoleProtectedRoute requirePerm="admin" user={user}>
                <FSMAdmin />
              </RoleProtectedRoute>
            } />
            
            {/* Retail Analytics / Lojas */}
            <Route path="lojas" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailDashboard />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/vendas" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailVendas />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/custos" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailCustos />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/analises" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailAnalises />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/fecho" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailFechoCaixa />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/depositos" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailDepositos />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/controle" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailControleDiario />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/auditoria" element={
              <RoleProtectedRoute requirePerm="analytics.view" user={user}>
                <RetailAuditLog />
              </RoleProtectedRoute>
            } />
            <Route path="lojas/configuracao" element={
              <RoleProtectedRoute requirePerm="analytics.import" user={user}>
                <RetailConfigAdmin />
              </RoleProtectedRoute>
            } />
            
            <Route path="admin/data-quality" element={
              <RoleProtectedRoute requirePerm="data_quality.view" user={user}>
                <DataQuality />
              </RoleProtectedRoute>
            } />
            <Route path="admin/procurement" element={
              <RoleProtectedRoute requirePerm="procurement.governance" user={user}>
                <ProcurementConfig />
              </RoleProtectedRoute>
            } />
            <Route path="admin/unidades-compra" element={
              <RoleProtectedRoute requirePerm="procurement.governance" user={user}>
                <PurchaseUnits />
              </RoleProtectedRoute>
            } />
            <Route path="admin/audit-log" element={
              <RoleProtectedRoute requirePerm="audit.view" user={user}>
                <AuditLog />
              </RoleProtectedRoute>
            } />
            <Route path="admin/go-live" element={
              <RoleProtectedRoute allowedRoles={['admin']} user={user}>
                <GoLiveAdmin />
              </RoleProtectedRoute>
            } />
            <Route path="help" element={<Help />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  );
}

export default App;
